
import Vue from "vue";
import unescape from "lodash/unescape";
import { cloneDeep } from "@/helpers/object";

import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  BulletList,
  Heading,
  ListItem,
  Bold,
  Italic,
  History
} from "tiptap-extensions";

import {
  BlueBachColorMark,
  IndigoColorMark,
  OrangeColorMark
} from "@/plugins/tiptap-color-plugin";

export default Vue.extend({
  components: {
    EditorContent,
    EditorMenuBar
  },
  props: {
    value: {
      type: String
    },
    characterLimit: {
      type: Number,
      default: 0
    },
    characterCount: {
      type: Number,
      default: 0
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    // Using self so I don't have to repeat (this as any) 4 times below...
    const self = this as any;

    return {
      valueUpdated: false,
      editor: new Editor({
        editable: true, // without being set to true initially it doesn't work
        content: JSON.parse(self.value),
        onBlur: self.onEditorBlur,
        onUpdate: self.onEditorUpdate,
        extensions: [
          new BlueBachColorMark(),
          new IndigoColorMark(),
          new OrangeColorMark(),
          new BulletList(),
          new Heading({ maxLevel: 1 }),
          new ListItem(),
          new Bold(),
          new Italic(),
          new History()
        ]
      })
    };
  },
  watch: {
    // Need to watch changes to editable prop and manually set the editor
    // options as it doesn't pick up the changes via reactivity
    "$props.editable": function(value) {
      this.editor.options.editable = Boolean(value);
    },
    "value": function(val) {
      // The watch will get triggered each time the value is updated, whether
      // it's because the value changed externally via an API call, or because this
      // component raised an "input" event. The valueUpdate flag is used to prevent setContent
      // being called when the value was changed by the component itself.
      if (val && !this.valueUpdated) {
        // Update the editor content and the character count
        this.editor.setContent(JSON.parse(val));
        this.updateCharacterCount();
      }

      // Reset flag for the next update.
      this.valueUpdated = false;
    }
  },
  methods: {
    // When the editor loses focus
    onEditorBlur() {
      const json = this.editor.getJSON();

      if (!json) {
        return;
      }

      // mark as updated so the watch knows the change occurred internally
      this.valueUpdated = true;

      // convert back to a string for the model
      this.$emit("input", JSON.stringify(json));
    },
    // When something is typed or changed in the editor
    onEditorUpdate() {
      this.updateCharacterCount();
    },
    updateCharacterCount() {
      // get the character count by grabbing the HTML and
      // stripping out the tags using a regex
      const html = this.editor.getHTML();

      if (!html) {
        return 0;
      }

      // Set our raw html version.
      this.$emit("update:valueHtml", html);

      // Use regex to remove html tags, and unescape to transform special
      // characters like &nbsp; to "&".
      const count = unescape(html.replace(/(<([^>]+)>)/gi, "")).length;

      this.$emit("update:characterCount", count);
    }
  },
  mounted() {
    // update the character count when the editor is ready.
    this.updateCharacterCount();
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  }
});
